/**
 * PD-17137 — where a storefront's texting disclosure links. US carriers check
 * that every place a customer gives a mobile number for texts shows the
 * disclosure with links to the texting terms and privacy policy, and they
 * rejected HF Foods' campaign (Twilio error 30909) when it did not.
 *
 * Each storefront serves its own Text Messages page under the brand's name, so a
 * brand without a website of its own still has terms to link to, and a
 * subsidiary brand never points at its parent company's site. A brand that
 * publishes its own texting terms and privacy policy (HF Foods, on
 * hffoodsgroup.com) sets them on its portal site instead, and the links go
 * there. The server, the portal site editor and the storefront share these
 * rules so a link that one accepts is never one another refuses.
 */

/** The storefront path of the Text Messages page. */
export const TEXT_MESSAGES_PATH = '/text-messages';

/** The element id of the privacy part of the Text Messages page. */
export const TEXT_MESSAGES_PRIVACY_ANCHOR = 'privacy';

const MAX_LINK_LENGTH = 512;

/** A storefront's own texting policy links; null means the Text Messages page. */
export interface TextMessagePolicyLinksModel {
	termsUrl: string | null;
	privacyUrl: string | null;
}

/** Where one disclosure link goes, and whether that is another site. */
export interface TextMessagePolicyHrefModel {
	href: string;
	external: boolean;
}

/**
 * A policy link as stored: a trimmed https link, or null for anything else.
 * Only https is accepted because the link is shown to customers as the
 * business's policy; a plain http or script link has no place there.
 */
export function normalizeTextMessagePolicyLink(value: unknown): string | null {
	if (typeof value !== 'string') {
		return null;
	}
	const trimmed = value.trim();
	if (trimmed === '' || trimmed.length > MAX_LINK_LENGTH) {
		return null;
	}
	try {
		const url = new URL(trimmed);
		return url.protocol === 'https:' && url.hostname !== '' ? trimmed : null;
	} catch {
		return null;
	}
}

/** True for a blank value (no link of its own) or a valid https link. */
export function isValidOptionalTextMessagePolicyLink(value: unknown): boolean {
	if (value == null || (typeof value === 'string' && value.trim() === '')) {
		return true;
	}
	return normalizeTextMessagePolicyLink(value) != null;
}

/** The policy links from a portal site's settings, validated. */
export function textMessagePolicyLinksFromSettings(
	settings:
		| { textMessagesTermsUrl?: unknown; textMessagesPrivacyUrl?: unknown }
		| null
		| undefined,
): TextMessagePolicyLinksModel {
	return {
		termsUrl: normalizeTextMessagePolicyLink(settings?.textMessagesTermsUrl),
		privacyUrl: normalizeTextMessagePolicyLink(
			settings?.textMessagesPrivacyUrl,
		),
	};
}

/**
 * Where the disclosure's Terms and Privacy links go: the brand's own policy
 * when it has one, otherwise the storefront's Text Messages page. Each link
 * falls back on its own, so a brand that publishes only one still links both.
 */
export function textMessagePolicyHrefs(
	links: Partial<TextMessagePolicyLinksModel> | null | undefined,
): { terms: TextMessagePolicyHrefModel; privacy: TextMessagePolicyHrefModel } {
	const terms = normalizeTextMessagePolicyLink(links?.termsUrl);
	const privacy = normalizeTextMessagePolicyLink(links?.privacyUrl);
	return {
		terms:
			terms != null
				? { href: terms, external: true }
				: { href: TEXT_MESSAGES_PATH, external: false },
		privacy:
			privacy != null
				? { href: privacy, external: true }
				: {
						href: `${TEXT_MESSAGES_PATH}#${TEXT_MESSAGES_PRIVACY_ANCHOR}`,
						external: false,
					},
	};
}
